import React, { useState, useRef, useEffect } from 'react';
import './Navbar.css';
import Logo from "../assets/logo v4.png";
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Select, MenuItem, Avatar, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from '@mui/material';
import ClassIcon from '@mui/icons-material/Class';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import InventoryIcon from '@mui/icons-material/Inventory';
import SchoolIcon from '@mui/icons-material/School';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import user from '../assets/user.png';
import edit from '../assets/edit.png';
import EditProfileModal from './EditProfileModal';

function stringToColor(string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

function stringAvatar(name) {
  return {
    sx: {
      bgcolor: stringToColor(name),
      fontSize: '0.875rem', // 可以調整這個值來改變字體大小，例如：'0.875rem' (較小) 或 '1.2rem' (較大)
    },
    children: `${name.split('').map((char, index) => index > 0 ? char : '').join('')}`,
  };
}

function DropdownItem({ img = '', text = '', onClick = null }) {
  return (
    <li className="dropdownItem" onClick={onClick}>
      <img src={img} alt={text} />
      <span
      style={{
        color: 'black',
        textDecoration: 'none',
        fontSize: '1rem',
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif',
        textTransform: 'none',
        textAlign: 'left',
      }}
      
      >{text}</span>
    </li>
  );
}

/**
 * Navbar 統一接收 menuGroups prop，橫向顯示多群組選單。
 * menuGroups 格式：
 * [
 *   {
 *     title: '群組標題',
 *     items: [
 *       { icon: ClassIcon, text: '任務清單', active: false, onClick: () => {} },
 *       ...
 *     ]
 *   },
 *   ...
 * ]
 */
const Navbar = ({ menuGroups = [], allPhaseMenuGroups = null, currentPhase = 'preparation', onPhaseChange, currentCourseId = 1, onCourseChange }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [navbarCollapsed, setNavbarCollapsed] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [localCurrentPhase, setLocalCurrentPhase] = useState(currentPhase); // 本地狀態用於側欄展開
  const avatarRef = useRef();
  const sidebarRef = useRef();
  const signOut = useSignOut();
  const navigate = useNavigate();
  
  const name = localStorage.getItem('name') || 'ET';
  const email = localStorage.getItem('email') || '';

  // 同步外部 currentPhase 到本地狀態
  useEffect(() => {
    setLocalCurrentPhase(currentPhase);
  }, [currentPhase]);

  // 動態調整 main-content-v2 的 margin-top（當導覽列收起時設為 0）
  useEffect(() => {
    const mainContent = document.querySelector('.main-content-v2');
    if (mainContent) {
      if (navbarCollapsed) {
        mainContent.style.marginTop = '0';
      } else {
        mainContent.style.marginTop = '64px';
      }
    }
    return () => {
      // 清理時恢復預設值
      if (mainContent) {
        mainContent.style.marginTop = '';
      }
    };
  }, [navbarCollapsed]);

  // 處理滑鼠離開自動收合
  useEffect(() => {
    const handleClick = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setSidebarOpen(false);
      }
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarOpen(false);
      }
    };
    
    if (sidebarOpen || avatarOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    
    return () => document.removeEventListener('mousedown', handleClick);
  }, [sidebarOpen, avatarOpen]);
  
  // 處理導覽列自動收起展開（A-2）
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // 檢查主內容區域的滾動（因為 main-content-v2 有自己的滾動容器）
          const mainContent = document.querySelector('.main-content-v2');
          const currentScrollY = mainContent ? mainContent.scrollTop : window.scrollY || window.pageYOffset;
          
          // 滑動到下方時自動收起（向下滾動超過 100px）
          if (currentScrollY > 100 && currentScrollY > lastScrollY) {
            setNavbarCollapsed(true);
          }
          // 滑回上方時自動展開（向上滾動或回到頂部）
          else if (currentScrollY < lastScrollY || currentScrollY < 50) {
            setNavbarCollapsed(false);
          }
          
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    // 監聽主內容區域的滾動事件（如果存在）
    const mainContent = document.querySelector('.main-content-v2');
    const scrollElement = mainContent || window;
    
    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    
    // 也監聽 window 的滾動（以防萬一）
    if (mainContent) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
      if (mainContent) {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [lastScrollY]);
  
  // 處理側欄階段標題點擊（只展開選單，不導航）
  const handleSidebarPhaseClick = (phase) => {
    setLocalCurrentPhase(phase);
    // 不調用 onPhaseChange，只更新本地狀態來展開選單
  };

  // 處理上方按鈕點擊（需要導航）
  const handlePhaseChange = (phase) => {
    setLocalCurrentPhase(phase);
    if (onPhaseChange) {
      onPhaseChange(phase);
    } else {
      // 默認導航行為
      switch (phase) {
        case 'preparation':
          navigate('/teacher/preparation');
          break;
        case 'implementation':
          navigate('/teacher/implementation');
          break;
        case 'diagnosis':
          navigate('/teacher/diagnosis');
          break;
        default:
          break;
      }
    }
  };

  const handleCourseChange = (courseId) => {
    if (onCourseChange) {
      onCourseChange(courseId);
    } else {
      // 默認行為：重新載入當前頁面但更新課程ID
      const currentPath = window.location.pathname;
      const newPath = currentPath.replace(/\/\d+(\/|$)/, `/${courseId}$1`);
      navigate(newPath);
    }
  };

  const handleOpenEditProfile = () => {
    setIsEditProfileOpen(true);
    setAvatarOpen(false);
  };

  const handleCloseEditProfile = () => {
    setIsEditProfileOpen(false);
  };

  const handleOpenReport = () => {
    setIsReportOpen(true);
    setAvatarOpen(false);
  };

  const handleCloseReport = () => {
    setIsReportOpen(false);
    setReportTitle('');
    setReportDescription('');
  };

  const developerEmail = 'happyjenny12347@gmail.com';

  const buildReportPayload = () => {
      const subject = `[問題回報] ${reportTitle || '無標題'}`;

      const bodyLines = [
          `姓名：${name || ''}`,
          `信箱：${email || ''}`,
          `頁面：${window.location.pathname}`,
          `時間：${new Date().toLocaleString('zh-TW')}`,
          '',
          '問題標題：',
          reportTitle || '',
          '',
          '問題說明：',
          reportDescription || '',
      ];

      return {
          to: developerEmail,
          subject,
          body: bodyLines.join('\n'),
      };
  };

  const openMailto = () => {
      const { to, subject, body } = buildReportPayload();
      const href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = href;
      handleCloseReport();
  };

  const openGmailCompose = () => {
      const { to, subject, body } = buildReportPayload();
      const gmailUrl =
          `https://mail.google.com/mail/?view=cm&fs=1` +
          `&to=${encodeURIComponent(to)}` +
          `&su=${encodeURIComponent(subject)}` +
          `&body=${encodeURIComponent(body)}`;

      window.open(gmailUrl, '_blank', 'noopener,noreferrer');
      handleCloseReport();
  };

  const logout = () => {
    signOut();
    navigate("/");
    localStorage.removeItem('userId');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('activityId');
    localStorage.removeItem('groupId');
    localStorage.removeItem('nodeId');
    localStorage.removeItem('nodeDataLength');
    localStorage.removeItem('jwtToken');
  };
  
  return (
    <>
      {/* 上方平台標題、logo與頭像 */}
      <nav className="main-navbar modern-navbar" style={{
        position:'fixed',
        top: navbarCollapsed ? '-64px' : '0',
        left:0,
        right:0,
        height:64,
        display:'flex',
        alignItems:'center',
        justifyContent:'space-between',
        background:'#2b687f',
        color:'#fff',
        zIndex:2000,
        boxSizing: 'border-box',
        transition: 'top 0.3s ease-in-out',
        boxShadow: navbarCollapsed ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'visible'
      }}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <img alt="logo" src={Logo} className="navbar-logo circle" style={{width:40,height:40,background:'#fff'}} />
          <span className="navbar-title" style={{fontSize:'1.35rem',fontWeight:'bold',letterSpacing:2,color:'#fff'}}>想法探究平台</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          {/* 教學階段導覽 */}
          <div className="teaching-phase-navigation" style={{display:'flex',alignItems:'center',gap:16}}>
            <button 
              className={`phase-button ${currentPhase === 'preparation' ? 'active' : ''}`}
              onClick={() => handlePhaseChange('preparation')}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                backgroundColor: currentPhase === 'preparation' ? '#1976d2' : 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📁 課前準備
            </button>
            <button 
              className={`phase-button ${currentPhase === 'implementation' ? 'active' : ''}`}
              onClick={() => handlePhaseChange('implementation')}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                backgroundColor: currentPhase === 'implementation' ? '#1976d2' : 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              ▶️ 課中實施
            </button>
            <button 
              className={`phase-button ${currentPhase === 'diagnosis' ? 'active' : ''}`}
              onClick={() => handlePhaseChange('diagnosis')}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                backgroundColor: currentPhase === 'diagnosis' ? '#1976d2' : 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📊 課後診斷
            </button>
          </div>
          {/* 頭像下拉選單 */}
          <div ref={avatarRef} style={{position: 'relative', display: 'flex', alignItems: 'center', zIndex: 2001}}>
            <div className="menu-trigger" onClick={() => setAvatarOpen(!avatarOpen)}>
              <Avatar {...stringAvatar(name)} />
            </div>
            <div className={`dropdown-menu ${avatarOpen ? 'active' : 'inactive'}`} style={{zIndex: 2001}}>
              <h3>嗨！{name}</h3>
              <ul>
                <DropdownItem
                  img={edit}
                  text={"編輯個人資料"}
                  onClick={handleOpenEditProfile}
                />
                <DropdownItem
                  img={edit}
                  text={"問題回報"}
                  onClick={handleOpenReport}
                />
              </ul>
              <h3><button className='register-button1' onClick={logout}>登出</button></h3>
            </div>
          </div>
        </div>
      </nav>
      {/* 左下側側邊欄，支援收合，滑鼠離開自動收合 */}
      <aside ref={sidebarRef} className={`navbar-sidebar${sidebarOpen ? '' : ' collapsed'}`} style={{
        position:'fixed',
        top: navbarCollapsed ? '0' : '64px',
        left:0,
        bottom:0,
        width:sidebarOpen?220:60,
        minWidth:sidebarOpen?220:60,
        background:'#f7fafd',
        borderRight:'1.5px solid #e0e6ed',
        display:'flex',
        flexDirection:'column',
        height: navbarCollapsed ? '100vh' : 'calc(100vh - 64px)',
        zIndex:1000,
        transition:'width 0.2s, top 0.3s ease-in-out, height 0.3s ease-in-out'
      }}>
        <div className="sidebar-header" style={{height:32}}></div>
        <button className="sidebar-toggle-btn" onClick={() => setSidebarOpen(o => !o)} style={{
          background:'none',
          border:'none',
          cursor:'pointer',
          fontSize:22,
          margin:'32px auto 12px auto',
          display:'block'
        }}>
          {sidebarOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </button>
        {/* A-3: 支援分層架構 - 如果傳入 allPhaseMenuGroups，顯示所有三個階段 */}
        {allPhaseMenuGroups ? (
          // 分層架構模式：顯示課前準備、課中實施、課後分析三個分類
          <>
            {allPhaseMenuGroups.preparation && allPhaseMenuGroups.preparation.map((group, idx) => (
              <div key={`preparation-${group.title}`} className="sidebar-group">
                {sidebarOpen && (
                  <div 
                    className="sidebar-title" 
                    style={{
                      fontWeight:'bold',
                      color: localCurrentPhase === 'preparation' ? '#2b687f' : '#999',
                      margin:'8px 0 4px 16px',
                      fontSize: '0.9rem',
                      opacity: localCurrentPhase === 'preparation' ? 1 : 0.6,
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'opacity 0.2s ease'
                    }}
                    onClick={() => handleSidebarPhaseClick('preparation')}
                    onMouseEnter={(e) => {
                      if (localCurrentPhase !== 'preparation') {
                        e.target.style.opacity = '0.8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (localCurrentPhase !== 'preparation') {
                        e.target.style.opacity = '0.6';
                      }
                    }}
                  >
                    {group.title}
                  </div>
                )}
                <ul style={{paddingLeft:sidebarOpen?16:0, display: localCurrentPhase === 'preparation' ? 'block' : 'none'}}>
                  {group.items.map((item, i) => {
                    const tooltipText = item.tooltip || item.text;
                    const menuItem = (
                      <li key={item.text} className={item.active ? 'active' : ''} style={{
                        display:'flex',
                        alignItems:'center',
                        justifyContent:sidebarOpen?'flex-start':'center',
                        padding:sidebarOpen?'4px 8px':'4px 0',
                        minHeight:40,
                        height:40,
                        cursor:'pointer',
                        borderRadius:6,
                        background:item.active?'#eaf2f5':'none',
                        color:item.active?'#2b687f':'#444',
                        fontWeight:item.active?'bold':'normal',
                        marginBottom:sidebarOpen?4:2,
                        lineHeight:'1.2',
                        gap:sidebarOpen?12:0
                      }} onClick={item.onClick}>
                        <span className="sidebar-icon" style={{
                          fontSize:sidebarOpen?22:24,
                          margin:0,
                          display:'inline-flex',
                          alignItems:'center',
                          justifyContent:'center',
                          width:40,
                          height:40,
                          textAlign:'center',
                          flexShrink:0
                        }}>
                          {React.createElement(item.icon, {
                            style: { fontSize: sidebarOpen ? 22 : 24 }
                          })}
                        </span>
                        {sidebarOpen && <span className="sidebar-text" style={{marginLeft:0}}>{item.text}</span>}
                      </li>
                    );
                    
                    return sidebarOpen ? menuItem : (
                      <Tooltip key={item.text} title={tooltipText} placement="right" arrow enterDelay={300}>
                        {menuItem}
                      </Tooltip>
                    );
                  })}
                </ul>
              </div>
            ))}
            {allPhaseMenuGroups.implementation && allPhaseMenuGroups.implementation.map((group, idx) => (
              <div key={`implementation-${group.title}`} className="sidebar-group">
                {sidebarOpen && (
                  <div 
                    className="sidebar-title" 
                    style={{
                      fontWeight:'bold',
                      color: localCurrentPhase === 'implementation' ? '#2b687f' : '#999',
                      margin:'8px 0 4px 16px',
                      fontSize: '0.9rem',
                      opacity: localCurrentPhase === 'implementation' ? 1 : 0.6,
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'opacity 0.2s ease'
                    }}
                    onClick={() => handleSidebarPhaseClick('implementation')}
                    onMouseEnter={(e) => {
                      if (localCurrentPhase !== 'implementation') {
                        e.target.style.opacity = '0.8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (localCurrentPhase !== 'implementation') {
                        e.target.style.opacity = '0.6';
                      }
                    }}
                  >
                    {group.title}
                  </div>
                )}
                <ul style={{paddingLeft:sidebarOpen?16:0, display: localCurrentPhase === 'implementation' ? 'block' : 'none'}}>
                  {group.items.map((item, i) => {
                    const isDisabled = item.disabled === true;
                    const tooltipText = isDisabled ? '請先選擇課程' : (item.tooltip || item.text);
                    const menuItem = (
                      <li 
                        key={item.text} 
                        className={item.active ? 'active' : ''} 
                        style={{
                          display:'flex',
                          alignItems:'center',
                          justifyContent:sidebarOpen?'flex-start':'center',
                          padding:sidebarOpen?'4px 8px':'4px 0',
                          minHeight:40,
                          height:40,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          borderRadius:6,
                          background:item.active?'#eaf2f5':'none',
                          color: isDisabled ? '#ccc' : (item.active?'#2b687f':'#444'),
                          fontWeight:item.active?'bold':'normal',
                          marginBottom:sidebarOpen?4:2,
                          lineHeight:'1.2',
                          gap:sidebarOpen?12:0,
                          opacity: isDisabled ? 0.5 : 1,
                          pointerEvents: isDisabled ? 'none' : 'auto'
                        }} 
                        onClick={isDisabled ? undefined : item.onClick}
                      >
                        <span className="sidebar-icon" style={{
                          fontSize:sidebarOpen?22:24,
                          margin:0,
                          display:'inline-flex',
                          alignItems:'center',
                          justifyContent:'center',
                          width:40,
                          height:40,
                          textAlign:'center',
                          flexShrink:0
                        }}>
                          {React.createElement(item.icon, {
                            style: { fontSize: sidebarOpen ? 22 : 24 }
                          })}
                        </span>
                        {sidebarOpen && <span className="sidebar-text" style={{marginLeft:0}}>{item.text}</span>}
                      </li>
                    );
                    
                    return sidebarOpen ? menuItem : (
                      <Tooltip key={item.text} title={tooltipText} placement="right" arrow enterDelay={300}>
                        {menuItem}
                      </Tooltip>
                    );
                  })}
                </ul>
              </div>
            ))}
            {allPhaseMenuGroups.diagnosis && allPhaseMenuGroups.diagnosis.map((group, idx) => (
              <div key={`diagnosis-${group.title}`} className="sidebar-group">
                {sidebarOpen && (
                  <div 
                    className="sidebar-title" 
                    style={{
                      fontWeight:'bold',
                      color: localCurrentPhase === 'diagnosis' ? '#2b687f' : '#999',
                      margin:'8px 0 4px 16px',
                      fontSize: '0.9rem',
                      opacity: localCurrentPhase === 'diagnosis' ? 1 : 0.6,
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'opacity 0.2s ease'
                    }}
                    onClick={() => handleSidebarPhaseClick('diagnosis')}
                    onMouseEnter={(e) => {
                      if (localCurrentPhase !== 'diagnosis') {
                        e.target.style.opacity = '0.8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (localCurrentPhase !== 'diagnosis') {
                        e.target.style.opacity = '0.6';
                      }
                    }}
                  >
                    {group.title}
                  </div>
                )}
                {/* 所有階段都顯示，但只有當前階段展開 */}
                <ul style={{paddingLeft:sidebarOpen?16:0, display: sidebarOpen && localCurrentPhase === 'diagnosis' ? 'block' : 'none'}}>
                  {group.items.map((item, i) => {
                    const isDisabled = item.disabled === true;
                    const tooltipText = isDisabled ? '此功能暫時無法使用' : (item.tooltip || item.text);
                    const menuItem = (
                      <li 
                        key={item.text} 
                        className={item.active ? 'active' : ''} 
                        style={{
                          display:'flex',
                          alignItems:'center',
                          justifyContent:sidebarOpen?'flex-start':'center',
                          padding:sidebarOpen?'4px 8px':'4px 0',
                          minHeight:40,
                          height:40,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          borderRadius:6,
                          background:item.active?'#eaf2f5':'none',
                          color: isDisabled ? '#ccc' : (item.active?'#2b687f':'#444'),
                          fontWeight:item.active?'bold':'normal',
                          marginBottom:sidebarOpen?4:2,
                          lineHeight:'1.2',
                          gap:sidebarOpen?12:0,
                          opacity: isDisabled ? 0.5 : 1,
                          pointerEvents: isDisabled ? 'none' : 'auto'
                        }} 
                        onClick={isDisabled ? undefined : item.onClick}
                      >
                        <span className="sidebar-icon" style={{
                          fontSize:sidebarOpen?22:24,
                          margin:0,
                          display:'inline-flex',
                          alignItems:'center',
                          justifyContent:'center',
                          width:40,
                          height:40,
                          textAlign:'center',
                          flexShrink:0
                        }}>
                          {React.createElement(item.icon, {
                            style: { fontSize: sidebarOpen ? 22 : 24 }
                          })}
                        </span>
                        {sidebarOpen && <span className="sidebar-text" style={{marginLeft:0}}>{item.text}</span>}
                      </li>
                    );
                    
                    return sidebarOpen ? menuItem : (
                      <Tooltip key={item.text} title={tooltipText} placement="right" arrow enterDelay={300}>
                        {menuItem}
                      </Tooltip>
                    );
                  })}
                </ul>
              </div>
            ))}
          </>
        ) : (
          // 原有模式：只顯示傳入的 menuGroups
          menuGroups.map((group, idx) => (
          <div key={group.title} className="sidebar-group">
            {sidebarOpen && <div className="sidebar-title" style={{fontWeight:'bold',color:'#2b687f',margin:'8px 0 4px 16px'}}>{group.title}</div>}
            <ul style={{paddingLeft:sidebarOpen?16:0}}>
              {group.items.map((item, i) => (
                <li key={item.text} className={item.active ? 'active' : ''} style={{
                  display:'flex',
                  alignItems:'center',
                  justifyContent:sidebarOpen?'flex-start':'center',
                  padding:sidebarOpen?'4px 8px':'4px 0',
                  minHeight:40,
                  height:40,
                  cursor:'pointer',
                  borderRadius:6,
                  background:item.active?'#eaf2f5':'none',
                  color:item.active?'#2b687f':'#444',
                  fontWeight:item.active?'bold':'normal',
                  marginBottom:2,
                  lineHeight:'1.2',
                  gap:sidebarOpen?12:0
                }} onClick={item.onClick}>
                  <span className="sidebar-icon" style={{
                    fontSize:sidebarOpen?22:24,
                    margin:0,
                    display:'inline-flex',
                    alignItems:'center',
                    justifyContent:'center',
                    width:40,
                    height:40,
                    textAlign:'center',
                    flexShrink:0
                  }}>
                    {React.createElement(item.icon, {
                      style: { fontSize: sidebarOpen ? 22 : 24 }
                    })}
                  </span>
                  {sidebarOpen && <span className="sidebar-text" style={{marginLeft:0}}>{item.text}</span>}
                </li>
              ))}
            </ul>
          </div>
          ))
        )}
      </aside>
      <EditProfileModal open={isEditProfileOpen} onClose={handleCloseEditProfile} />
      <Dialog open={isReportOpen} onClose={handleCloseReport}>
        <DialogTitle sx={{ color: 'black', textDecoration: 'none' }}>問題回報</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="reportTitle"
            label="問題標題"
            type="text"
            fullWidth
            variant="standard"
            value={reportTitle}
            onChange={(e) => setReportTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            id="reportDescription"
            label="詳細說明"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="standard"
            value={reportDescription}
            onChange={(e) => setReportDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleCloseReport}>取消</Button>

          {/* <Button
            onClick={openMailto}
            variant="outlined"
            disabled={!reportTitle || !reportDescription}
          >
            開啟預設信箱
          </Button> */}

          <Button
            onClick={openGmailCompose}
            variant="contained"
            disabled={!reportTitle || !reportDescription}
          >
            用 Gmail 回報
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar; 